import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    IRetryPolicy,
    LogLevel,
} from '@microsoft/signalr';
import { MessagePackHubProtocol } from '@microsoft/signalr-protocol-msgpack';
import AuthService from '../auth/Auth.services';
import { SignalRProtocol } from './types';

type JoinHandler = () => Promise<void>;

type ManagedConnection = {
    connection: HubConnection;
    refCount: number;
    joinHandlers: Map<string, JoinHandler>;
    startPromise: Promise<void> | null;
};

const reconnectPolicy: IRetryPolicy = {
    nextRetryDelayInMilliseconds: (retryContext) => {
        const delays = [0, 2000, 10000, 30000];
        if (retryContext.previousRetryCount >= delays.length) {
            return 30000;
        }
        return delays[retryContext.previousRetryCount];
    },
};

class SignalRConnectionManager {
    private readonly connections = new Map<string, ManagedConnection>();

    private poolKey(hubUrl: string, protocol: SignalRProtocol): string {
        return `${protocol}::${hubUrl}`;
    }

    private async resolveAccessToken(): Promise<string> {
        const fromStorage = localStorage.getItem('access_token');
        if (fromStorage) {
            return fromStorage;
        }

        try {
            const authService = new AuthService();
            return (await authService.getAccessToken()) || '';
        } catch {
            return '';
        }
    }

    async acquire(hubUrl: string, protocol: SignalRProtocol = 'json'): Promise<HubConnection> {
        const key = this.poolKey(hubUrl, protocol);
        let managed = this.connections.get(key);

        if (!managed) {
            const builder = new HubConnectionBuilder()
                .withUrl(hubUrl, {
                    accessTokenFactory: () => this.resolveAccessToken(),
                    withCredentials: false,
                })
                .withAutomaticReconnect(reconnectPolicy)
                .configureLogging(LogLevel.Information);

            console.info('[SignalR] connecting', { hubUrl, protocol });

            if (protocol === 'messagepack') {
                builder.withHubProtocol(new MessagePackHubProtocol());
            }

            const connection = builder.build();

            managed = {
                connection,
                refCount: 0,
                joinHandlers: new Map(),
                startPromise: null,
            };

            connection.onreconnected(async () => {
                const handlers = Array.from(managed!.joinHandlers.values());
                for (const join of handlers) {
                    try {
                        await join();
                    } catch (err) {
                        console.error('[SignalR] re-join after reconnect failed', err);
                    }
                }
            });

            this.connections.set(key, managed);
        }

        managed.refCount += 1;
        await this.ensureStarted(managed);
        return managed.connection;
    }

    async release(hubUrl: string, protocol: SignalRProtocol = 'json'): Promise<void> {
        const key = this.poolKey(hubUrl, protocol);
        const managed = this.connections.get(key);
        if (!managed) {
            return;
        }

        managed.refCount = Math.max(0, managed.refCount - 1);
        if (managed.refCount > 0) {
            return;
        }

        managed.joinHandlers.clear();
        try {
            if (managed.connection.state !== HubConnectionState.Disconnected) {
                await managed.connection.stop();
            }
        } finally {
            this.connections.delete(key);
        }
    }

    registerJoinHandler(hubUrl: string, groupKey: string, join: JoinHandler, protocol: SignalRProtocol = 'json'): void {
        const managed = this.connections.get(this.poolKey(hubUrl, protocol));
        if (managed) {
            managed.joinHandlers.set(groupKey, join);
        }
    }

    unregisterJoinHandler(hubUrl: string, groupKey: string, protocol: SignalRProtocol = 'json'): void {
        const managed = this.connections.get(this.poolKey(hubUrl, protocol));
        managed?.joinHandlers.delete(groupKey);
    }

    private async ensureStarted(managed: ManagedConnection): Promise<void> {
        if (managed.connection.state === HubConnectionState.Connected) {
            return;
        }

        if (!managed.startPromise) {
            managed.startPromise = managed.connection
                .start()
                .catch((err) => {
                    managed.startPromise = null;
                    throw err;
                });
        }

        await managed.startPromise;
    }
}

export const signalRConnectionManager = new SignalRConnectionManager();
